import { createSignal, For, Show, useContext } from 'solid-js';
import { Heading } from '../../ui-components/heading/heading';
import { Radio } from '../../ui-components/inputs/radio/radio';
import { FilterIcon } from '../icons-library/filter';
import { FilterList } from '../../ui-components/filter-list/filter-list';
import { MobileFilesFilterModal } from './mobile-files-filter-modal';
import { Category, Type } from './document-holder';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { MobileCategoryTag } from '../products/mobile/mobile-category-tag';
import { getFileSlugTranslations } from './file-translations-map';
import { useSearchParams } from '@solidjs/router';
import { getSearchParamAsArray } from './files';

export type MobileFilesFilterProps = {
    types: Type[];
    topCategories: Category[];
    subCategories: Category[];

    selectedTypeId?: number;

    clickHandlers: {
        handleTypeClick: (type: Type) => void;
        handleTopCategoryClick: (category: Category, action: 'add' | 'remove') => void;
        handleSubCategoryClick: (category: Category, action: 'add' | 'remove') => void;
    };

    clearFilters: (e: any) => void;
}


export const MobileFilesFilter = (props: MobileFilesFilterProps) => {
    const { localize } = useContext(AppContext);
    const [mobileFilterViewOpen, setMobileFilterViewOpen] = createSignal<boolean>(false);
    const [searchParams] = useSearchParams();

    let filterListRef: HTMLDivElement | undefined;

    const openMobileFilterView = () => {
        setMobileFilterViewOpen(true);
        const body = document.getElementsByTagName('body')[0];
        body.style.overflow = 'hidden';
    };

    const closeMobileFilterView = () => {
        setMobileFilterViewOpen(false);
        const body = document.getElementsByTagName('body')[0];
        body.style.overflow = '';
    };

    const selectedType = () => props.types.find(type => type.id === props.selectedTypeId);
    const selectedTopCategories = () => props.topCategories.map(category => getSearchParamAsArray(searchParams.categories).includes(category.name) ? category : null).filter(Boolean) as Category[];
    const selectedSubCategories = () => props.subCategories.map(category => getSearchParamAsArray(searchParams.subcategories).includes(category.name) ? category : null).filter(Boolean) as Category[];

    return (
        <>
            <FilterList
                isMobile={true}
                isDropdown={true}
                initialOpen={false}
                listHeading={selectedType()?.name
                    ? getFileSlugTranslations(localize, selectedType()!.name)
                    : localize('type', 'Type')
                }
                ref={filterListRef}
            >
                <For each={props.types}>{(type) => (
                    <li>
                        <Radio
                            value={type.uuid}
                            whenClicked={() => {
                                props.clickHandlers.handleTypeClick(type);
                                filterListRef?.click();
                            }}
                            name='type'
                            readableName={getFileSlugTranslations(localize, type.name)}
                            isChecked={type.id === props.selectedTypeId}
                        />
                    </li>
                )}</For>
            </FilterList>

            <Show when={props.topCategories.length > 0}>
                <div class="px-6">
                    <button
                        class="flex justify-center items-center bg-transparent p-4 border border-gray rounded-small w-full"
                        onClick={openMobileFilterView}
                        aria-label={localize('open-filters', 'Open Filters')}
                    >
                        <div class="relative flex items-center">
                            <div class="absolute -left-10 pr-4">
                                <FilterIcon ariaHidden />
                            </div>
                            <Heading tag='h5' variant='medium' noBlockSpacing={true}>
                                {localize('filters', 'Filters')}
                            </Heading>
                        </div>
                    </button>
                </div>
            </Show>

            <Show when={selectedType()}>
                <ul class="flex flex-wrap px-6 pt-6">
                    <MobileCategoryTag
                        category={selectedType() && selectedType()!.name
                            ? getFileSlugTranslations(localize, selectedType()!.name)
                            : undefined
                        }
                        onClick={(e: any) => props.clearFilters(e)}
                    />
                    <For each={selectedTopCategories()}>
                        {(category: Category) => (
                            <MobileCategoryTag
                                category={getFileSlugTranslations(localize, category.name)}
                                onClick={() => {
                                    props.clickHandlers.handleTopCategoryClick(category, 'remove');
                                }}
                            />
                        )}
                    </For>
                    <For each={selectedSubCategories()}>{(subCategory: Category) => (
                        <MobileCategoryTag
                            category={getFileSlugTranslations(localize, subCategory.name)}
                            onClick={() => {
                                props.clickHandlers.handleSubCategoryClick(subCategory, 'remove');
                            }}
                        />
                    )}</For>
                </ul>
            </Show>


            <Show when={mobileFilterViewOpen()}>
                <MobileFilesFilterModal
                    closeModal={closeMobileFilterView}
                    applyFiltersText={localize('apply-filters', 'Apply filters')}
                    topCategoriesHeading={localize('category', 'Category')}
                    subCategoriesHeading={localize('sub-category', 'Sub Category')}

                    topCategories={props.topCategories}
                    subCategories={props.subCategories}

                    clickHandlers={{
                        handleTopCategoryClick: props.clickHandlers.handleTopCategoryClick,
                        handleSubCategoryClick: props.clickHandlers.handleSubCategoryClick,
                    }}
                />
            </Show>
        </>
    );
};
